import {
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import useSWR from "swr"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { TActivityVoucherTemplate } from "./store"

export function XHR(xhr: XHRInstance) {
	return {
		async getActivityVoucherTemplateData(): Promise<
			Array<TActivityVoucherTemplate>
		> {
			return xhr
				.get(`/activity-voucher-templates`)
				.then(({ data }) => data.data)
		},
		async getActivityVoucherTemplateEditData(): Promise<TActivityVoucherTemplate> {
			return xhr.get(`activity-voucher-templates/create`).then(({ data }) => {
				return data.data
			})
		},
	}
}

export function ActivityVoucherTemplates() {
	const xhr = useXHR()
	const { data, mutate, isLoading } = useSWR(
		`/activity-voucher-templates`,
		() => XHR(xhr).getActivityVoucherTemplateData()
	)
	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	if (isLoading) {
		return <Spinner alignCenter padding="4" />
	}

	return (
		<Stack>
			<Inline alignItems="center" justifyContent={"between"}>
				<Inline alignItems={"center"} justifyContent={"center"} gap={"2"}>
					<Box
						padding={"3"}
						rounded="full"
						borderColor={"default"}
						borderWidth={"1"}
					>
						<Icons.Ticket color={"muted"} size={"10"} />
					</Box>
					<Stack>
						<Heading as="h3">Activity Voucher Template Configurations</Heading>
						<Text>
							These configuration will be applied to Activity Voucher only.
						</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button onClick={openDialog} status="primary" size="sm">
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
				<Dialog
					open={isDialogOpen}
					onClose={closeDialog}
					title="Update Activity Voucher"
					lg
				>
					<Dialog.Body>
						<EditActivityVoucherTemplate
							onCancel={() => {
								closeDialog()
							}}
							onSuccess={() => {
								closeDialog()
								mutate()
							}}
						/>
					</Dialog.Body>
				</Dialog>
			</Inline>
			<Divider sm />
			{!data || data?.length === 0 ? (
				<Box padding="4" bgColor="default" rounded="md" borderWidth="1">
					<Text>Using default activity voucher template configurations.</Text>
				</Box>
			) : (
				<Stack gap="4">
					<Inline>
						<Text color={"default"} fontWeight={"semibold"}>
							Title Customization:
						</Text>{" "}
						<Text color={"muted"}>{data[0].title}</Text>
					</Inline>
				</Stack>
			)}
		</Stack>
	)
}

const validationSchema = validateFormValues(
	Validator.object().shape({
		title: Validator.string().required("Please enter title name"),
	})
)

function EditActivityVoucherTemplate({
	onCancel,
	onSuccess,
}: {
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const { data } = useSWR(`/activity-voucher-templates/create`, () =>
		XHR(xhr).getActivityVoucherTemplateEditData()
	)
	if (!data) {
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Form<TActivityVoucherTemplate>
			onSubmit={withServerErrors(async (values) => {
				const { title } = values
				await xhr.post(`activity-voucher-templates`, {
					title,
				})
				onSuccess()
				showSnackbar("Successfully Changed")
			})}
			subscription={{ submitting: true }}
			initialValues={{
				title: data.title,
			}}
			validate={validationSchema}
		>
			{({ handleSubmit, submitting, form }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="6" paddingY="4">
						<Stack gap="4">
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading fontSize="md">Title Customization</Heading>
										<Text color="muted">
											Configure title settings like Tour Voucher, Final Trip
											Voucher and Service Voucher.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Component initialState={false}>
										{({ state: editing, setState }) =>
											!editing ? (
												<Stack gap="1">
													<Text fontWeight="semibold">Section Title</Text>
													<Inline
														borderWidth="1"
														rounded="md"
														paddingLeft="4"
														bgColor="subtle"
														gap="4"
														justifyContent="between"
														alignItems="center"
														maxWidth={"sm"}
													>
														<Text>{data.title}</Text>
														<Button
															onClick={() => {
																setState(true)
															}}
															level="tertiary"
														>
															<Icons.Pencil />
														</Button>
													</Inline>
												</Stack>
											) : (
												<TextInputField
													name="title"
													label="Section Title"
													type="text"
													maxWidth={"sm"}
												/>
											)
										}
									</Component>
								</Col>
							</Grid>
						</Stack>
						<Divider />
						<Stack>
							<SubmissionError />
							<Inline justifyContent="end" gap="2">
								<Button onClick={onCancel}>Cancel</Button>
								<Button status="primary" type="submit" disabled={submitting}>
									Save Changes
								</Button>
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}
